import * as React from 'react';
import { connect } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { isValidSKUFormat } from 'utils/realm-outfits';
import { isRequired, maxLength } from 'utils/validators';

import { fetchCampaignProductList, resetCampaignProductList, saveCampaignProductList } from '../../reducers/campaign-product-list';

import './product-list-modal.scss';

import type { CampaignProductListFragment } from 'types';

import type { ChangeEvent, ClipboardEvent } from 'react';

applyTranslations(require('./product-list-modal.i18n.json'));

const TITLE_MAX_LENGTH = 60;

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['span', 'br'],
  allowedAttributes: {
    span: ['class']
  }
};

type OwnProps = {
  listId: number,
  campaignId: number,
  onCloseModal: () => void
};

type StateProps = {
  productList: CampaignProductListFragment,
  productListLoading: boolean
};

type DispatchProps = {
  dispatchFetchCampaignProductList: (b: number, a: number) => void,
  dispatchResetCampaignProductList: () => void,
  dispatchSaveCampaignProductList: (c: number, b: number | undefined | null, a: CampaignProductListFragment) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type SKU = {
  label: string,
  isValid: boolean
};

type State = {
  title: string,
  isModified: boolean,
  skus: Array<SKU>,
  userChangedSKUs: boolean,
  isCleared: boolean
};

type ValidationFunction = (a: string) => boolean;

class ProductListModal extends React.PureComponent<Props, State> {
  state: State = {
    title: '',
    isModified: false,
    skus: [],
    userChangedSKUs: false,
    isCleared: false
  };

  contentRef: React.RefObject<any> = React.createRef();

  componentDidMount() {
    const { dispatchFetchCampaignProductList, campaignId, listId } = this.props;

    if (listId) {
      dispatchFetchCampaignProductList(campaignId, listId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { productList } = this.props;

    if (prevProps.productListLoading !== this.props.productListLoading && productList) {
      const skus = this.validateSKUs(productList.skus || []);

      this.setState({ // eslint-disable-line react/no-did-update-set-state
        skus,
        title: productList.title || ''
      });
    }
  }

  componentWillUnmount() {
    this.props.dispatchResetCampaignProductList();
  }

  getSKUs = () => ((this.contentRef.current?.innerText || '').trim() || null)?.split(/[\s,;]+/) ?? [];

  validateSKUs(skus: Array<string>, isValidSKU: ValidationFunction = isValidSKUFormat): Array<SKU> {
    return skus.map(sku => ({
      isValid: isValidSKU(sku),
      label: sku
    }));
  }

  stringifySKUs(skus: Array<SKU>): string {
    const labels = skus.map(item => (
      item.isValid ? item.label : `<span class="product-list-modal__invalid-sku">${item.label}</span>`
    ));
    return `${labels.join('\n')}\n`;
  }

  handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      title: event.target.value,
      isModified: true
    });
  };

  handlePaste = (event: ClipboardEvent<HTMLElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    document.execCommand('insertHTML', false, sanitizeHtml(text, SANITIZE_HTML_CONFIG));
  };

  handleSKUListUpdate = () => {
    this.setState({
      isCleared: false
    });
  };

  highlightInvalidSKUs = (invalidSKUs: Array<string>) => {
    this.setState({
      skus: this.validateSKUs(this.getSKUs(), sku => invalidSKUs.indexOf(sku) === -1)
    });
  };

  handleRemoveWrongSKUs = () => {
    const { skus } = this.state;
    this.setState({
      skus: skus.filter(item => item.isValid),
      isCleared: true
    });
  };

  handleSubmit = () => {
    const { campaignId, listId, dispatchSaveCampaignProductList, onCloseModal } = this.props;
    const skus = this.validateSKUs(this.getSKUs());
    const hasError = skus.some(item => !item.isValid);

    this.setState({ skus, userChangedSKUs: false });

    if (hasError) {
      return;
    }

    const productList: CampaignProductListFragment = {
      id: listId,
      title: this.state.title,
      skus: skus.map(item => item.label)
    };

    dispatchSaveCampaignProductList(campaignId, listId, productList)
      .then(() => onCloseModal())
      .catch(error => {
        if (error.response?.data?.code === 'unprocessable.skus') {
          const invalidSKUs = error.response?.data?.detail?.split(/,\s+/) || [];
          this.highlightInvalidSKUs(invalidSKUs);
        }
      });
  };

  renderForm() {
    const { onCloseModal } = this.props;
    const { title, isModified, skus, isCleared, userChangedSKUs } = this.state;
    const hasError = skus.some(item => !item.isValid);
    const howManyInvalid = skus.filter(item => !item.isValid).length;
    const textareaClasses = classnames({
      'product-list-modal__sku-message': true,
      'product-list-modal__sku-message--has-wrong-skus': hasError
    });

    return (
      <ModalFrameInner
        narrowContent
      >
        <Form
          onSubmit={this.handleSubmit}
        >
          {
            isModified && <PromptExit type="modal" />
          }
          <div className="product-list-modal__form-content">
            <FormField
              label={__t('product_list_modal.form.name_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isRequired(title),
                maxLength(title, TITLE_MAX_LENGTH)
              ]}
            >
              <Input
                width="responsive"
                value={title}
                placeholder={__t('product_list_modal.form.name_placeholder')}
                onChange={this.handleTitleChange}
              />
            </FormField>
            <FormField
              label={__t('product_list_modal.form.skus_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              showValidationIcon={false}
            >
              <pre
                key={skus.map(item => item.label).join('')}
                ref={this.contentRef}
                onInput={() => this.setState({ userChangedSKUs: true })}
                onBlur={this.handleSKUListUpdate}
                onPaste={this.handlePaste}
                className={textareaClasses}
                contentEditable
                data-placeholder={__t('product_list_modal.form.skus_placeholder')}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(this.stringifySKUs(skus), SANITIZE_HTML_CONFIG)
                }}
              />
            </FormField>
            {
              !userChangedSKUs && hasError && (
                <div className="product-list-modal__error-message">
                  {__t('product_list_modal.form.validation_sku_error', {
                    count: howManyInvalid,
                    withPlural: howManyInvalid
                  })}
                  <span
                    className="product-list-modal__error-message--link"
                    onClick={this.handleRemoveWrongSKUs}
                  >
                    {__t('product_list_modal.cleanup_link')}
                  </span>
                </div>
              )
            }
            {
              !userChangedSKUs && isCleared && (
                <div className="product-list-modal__successful-cleanup">
                  {__t('product_list_modal.successful_cleanup')}
                </div>
              )
            }
          </div>
          <ModalFrameFooter>
            <ActionButton
              color="tertiary"
              onClick={onCloseModal}
            >
              {__t('product_list_modal.cancel')}
            </ActionButton>
            <ActionButton
              color="secondary"
              type="submit"
            >
              {__t('product_list_modal.save')}
            </ActionButton>
          </ModalFrameFooter>
        </Form>
      </ModalFrameInner>
    );
  }

  render() {
    const { listId, productListLoading } = this.props;

    return (
      <ModalFrame
        title={__t(`product_list_modal.${listId ? 'edit' : 'add'}_title`)}
        classNameFrame="product-list-modal"
      >
        {
          productListLoading ?
            <Spinner
              position="inline"
              size="small"
              color="light"
            /> :
            this.renderForm()
        }
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ campaignProductList }): StateProps => ({
  productList: campaignProductList.list,
  productListLoading: campaignProductList.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaignProductList: (campaignId, listId) => dispatch(fetchCampaignProductList(campaignId, listId)),
  dispatchResetCampaignProductList: () => dispatch(resetCampaignProductList()),
  dispatchSaveCampaignProductList: (campaignId, listId, productList) => dispatch(saveCampaignProductList(campaignId, listId, productList))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductListModal);
